import React, {useContext, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";

import LookAiPopupContext from "./LookAiPopupContext";
import {IS_QKB_UI, getCommonVisualizerType} from "../../../../config";
import {buildAiAccordionSections, useAiGroupsAccordions} from "../../aiAccordeonGroups";
import {hideGroupObject} from "../../aiFunctions";
import {useAppSelector} from "../../../../storeHooks";
import LookAIPopupFeatureGroupRow from "./LookAIPopupFeatureGroupRow";
import LookAIPopupFeatureGroupAccordion from "./LookAIPopupFeatureGroupAccordion";
import {updateLocalStorage} from "../../../../misc/jobLocalStorageSave";


const LookAIPopupFeatureGroups = (props) => {

  const {setLookAiExportData} = props;

  const {lookAiPopupContext, lookAiPopupDispatch} = useContext(LookAiPopupContext);
  const {groupObjects} = lookAiPopupContext;

  const intl = useIntl();
  const fabricatorData = useAppSelector(state => state.fabricatorState);

  // Per-section expanded/collapsed state, keyed by accordion title. Sections default to
  // opened (true) - see buildAiAccordionSections - until the user minimizes one.
  const [openedSectionTitles, setOpenedSectionTitles] = useState({});

  const onSwitchClick = (groupObject) => (e) => {
    // e is a MouseEvent here -> altKey is reliable
    const isAlt = e.altKey === true;

    // optional: prevent MUI/input from toggling itself independently
    // (since we're fully controlling checked from state)
    e.preventDefault();
    e.stopPropagation();

    const newGroupObjects = isAlt
      // invert ALL
      ? groupObjects.map((go) => ({ ...go, enabled: !groupObject.enabled }))
      // invert ONE
      : groupObjects.map((go) =>
        go.partId === groupObject.partId ? { ...go, enabled: !go.enabled } : go
      );

    lookAiPopupDispatch({ groupObjects: newGroupObjects });
    setLookAiExportData(newGroupObjects);

    updateLocalStorage();
  };

  const onColorOnlyChange = (groupObject) => (e) => {
    const checked = e.target.checked;
    const newGroupObjects = groupObjects.map((go) =>
      go.partId === groupObject.partId ? { ...go, colorOnly: checked } : go
    );
    lookAiPopupDispatch({ groupObjects: newGroupObjects });
  };

  // Toggles every groupObject inside a section together: unchecked -> checks all of them,
  // checked (every group already enabled) -> unchecks all of them.
  const onSectionSwitchClick = (section) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    const nextEnabled = !section.groups.every(groupObject => !!groupObject.enabled);
    const sectionPartIds = new Set(section.groups.map(groupObject => groupObject.partId));
    const newGroupObjects = groupObjects.map((go) =>
      sectionPartIds.has(go.partId) ? { ...go, enabled: nextEnabled } : go
    );

    lookAiPopupDispatch({ groupObjects: newGroupObjects });
    setLookAiExportData(newGroupObjects);

    updateLocalStorage();
  };

  const onAccordionChange = (title) => (event, isExpanded) => {
    setOpenedSectionTitles(prev => ({ ...prev, [title]: isExpanded }));
  };

  const visibleGroupObjects = groupObjects ? groupObjects.filter(groupObject => !hideGroupObject(groupObject)) : [];
  // Flag off (default) -> skip grouping entirely and render exactly the pre-accordion flat list.
  const sections = useAiGroupsAccordions ? buildAiAccordionSections(visibleGroupObjects, getCommonVisualizerType()) : [];

  return (
    <div className="flex flex--column look-ai__content-groups" id="look-ai__panel-feature-groups">
      <div className="u-mt-0 u-mb-1 flex flex--column flex--top u-w-full">
        <p className={"u-m-0 bold" + (IS_QKB_UI ? " color-primary" : "")}>
          <FormattedMessage id="look.ai.panel.generateItems"/>
        </p>
        <p className="u-m-0 font--12 u-leading-1 look-ai__panel-text">
          <FormattedMessage id="look.ai.panel.generateItemsText"/>
        </p>
      </div>
      {useAiGroupsAccordions && sections.length > 1 ? (
        // More than one group of groups - render each as a minimize/maximize-able accordion,
        // with a parent toggle that summarizes (and can flip) every group inside it.
        sections.map(section => (
          <LookAIPopupFeatureGroupAccordion
            key={section.title}
            section={section}
            expanded={openedSectionTitles[section.title] ?? section.opened}
            onExpandChange={onAccordionChange(section.title)}
            onSectionToggle={onSectionSwitchClick(section)}
            intl={intl}
            fabricatorData={fabricatorData}
            onToggle={onSwitchClick}
            onColorOnlyChange={onColorOnlyChange}
          />
        ))
      ) : (
        <ul className="u-m-0 u-p-0 u-w-full">
          {visibleGroupObjects.map(groupObject => (
            <LookAIPopupFeatureGroupRow
              key={groupObject.featureGroup.partId}
              groupObject={groupObject}
              intl={intl}
              fabricatorData={fabricatorData}
              onToggle={onSwitchClick}
              onColorOnlyChange={onColorOnlyChange}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default LookAIPopupFeatureGroups;
